import { supabase } from '../config/supabase';
import { auth } from '../config/firebase';
import { FinancialRpcErrorInput, isFinancialRpcAbort, normalizeFinancialRpcError } from './financialRpcError';
import { FinancialPrimaryPayerConflictError, parseFinancialPrimaryPayerConflict } from './financialPayerPrimaryConflict';

export type FinancialSection = 'patients' | 'contracts' | 'packages' | 'invoices' | 'settlements'
  | 'events' | 'entitlements' | 'usages' | 'invoice_items' | 'settlement_items'
  | 'organizations' | 'payer_links' | 'pricing_rules' | 'settings' | 'services' | 'package_templates' | 'absence_policies';
export type FinancialRecord = { id: string; displayName: string | null; [key: string]: unknown };
export interface FinancialPage { records: FinancialRecord[]; total: number; pageSize: number }
export interface FinancialAppointmentSummary { appointmentId: string; appointmentStartDate: string; appointmentLocation: string | null; professionalName: string; patientName: string }
export interface FinancialCashAccount { cashAccountId: string; accountName: string; accountType: string; accountStatus: string }
export interface FinancialReceipt { receiptId: string; receiptStatus: string; amount: number; receivedAt: string; paymentMethod: string; notes: string | null; reversedReceiptId: string | null }
export interface FinancialPackageCancellationOption {
  financialPackageId: string;
  packageName: string;
  packageStatus: string;
  itemCount: number;
  invoiceCount: number;
  amountInSelectedInvoice: number;
  totalPackageAmount: number;
  selectedInvoiceHasOtherItems: boolean;
  canCancel: boolean;
  blockers: string[];
}
export interface FinancialPackageCancellationPreview {
  financialInvoiceId: string;
  packages: FinancialPackageCancellationOption[];
}
export interface FinancialCashOverview {
  periodFrom: string;
  periodUntil: string;
  totals: { openingBalance: number; inflow: number; outflow: number; closingBalance: number };
  accounts: Array<{ cashAccountId: string; accountName: string; accountType: string; openingBalance: number; inflow: number; outflow: number; closingBalance: number }>;
  movements: Array<{ cashMovementId: string; accountName: string; movementType: string; movementDirection: 'in' | 'out'; amount: number; occurredAt: string; description: string | null }>;
}

export interface FinancialSettlementPayment {
  settlementPaymentId: string;
  amount: number;
  paidAt: string;
  paymentMethod: string;
  cashAccountId: string;
  cashMovementId: string | null;
  paymentStatus: 'posted' | 'reversed';
  reversalOfPaymentId: string | null;
  notes: string | null;
}

export interface FinancialSettlementDetail {
  settlementId: string;
  professionalName: string;
  professionalId: string;
  status: string;
  periodFrom: string;
  periodUntil: string;
  totalSessions: number;
  totalAmount: number;
  paidAmount: number;
  remainingAmount: number;
  items: FinancialRecord[];
  payments: FinancialSettlementPayment[];
}

export interface FinancialSettlementPendingItem {
  appointmentId: string;
  appointmentStartDate: string;
  professionalId: string | null;
  professionalName: string;
  patientName: string;
  appointmentLocation: string | null;
  paymentOrigin: string;
  reason: string;
  existingSettlementStatus: string | null;
}

export interface FinancialSettlementPendingOverview {
  periodFrom: string;
  periodUntil: string;
  completedAppointments: number;
  includedAppointments: number;
  pendingAppointments: number;
  missingRuleAppointments: number;
  lockedSettlementAppointments: number;
  records: FinancialSettlementPendingItem[];
}

export type FinancialAbsenceCountingPeriod = 'calendar_month' | 'calendar_year' | 'rolling_days';
export type FinancialAbsencePackageTreatment = 'consume_credit' | 'monetary_charge' | 'no_charge';
export type FinancialAbsenceIndividualTreatment = 'contract_price' | 'percentage' | 'fixed_amount' | 'no_charge';

export interface FinancialAbsencePolicyValues {
  chargeFromAbsence: number;
  countingPeriodType: FinancialAbsenceCountingPeriod;
  rollingWindowDays: number | null;
  packageTreatment: FinancialAbsencePackageTreatment;
  individualTreatment: FinancialAbsenceIndividualTreatment;
  individualChargePercentage: number | null;
  individualFixedAmount: number | null;
  justificationDeadlineHours: number;
  generatesProfessionalSettlement: boolean;
}

export interface FinancialTenantAbsencePolicy extends FinancialAbsencePolicyValues {
  absencePolicyId: string;
  clientId: string;
  policyVersion: number;
  policyStatus: 'draft' | 'active' | 'closed';
  effectiveFrom: string;
  effectiveUntil: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string | null;
}

export interface FinancialContractAbsencePolicy extends Partial<FinancialAbsencePolicyValues> {
  contractAbsencePolicyId: string;
  clientId: string;
  contractId: string;
  policyMode: 'inherit' | 'no_charge' | 'custom';
  notes: string | null;
}

export interface FinancialTenantAbsencePoliciesResult {
  activePolicy: FinancialTenantAbsencePolicy | null;
  draftPolicy: FinancialTenantAbsencePolicy | null;
  history: FinancialTenantAbsencePolicy[];
  systemDefault: FinancialAbsencePolicyValues;
}

export interface FinancialContractAbsencePolicyResult {
  contractPolicy: FinancialContractAbsencePolicy | null;
  effectiveSource: 'contract_no_charge' | 'contract_custom' | 'tenant_policy' | 'system_default';
  effectivePolicy: FinancialAbsencePolicyValues & { absencePolicyId?: string; policyVersion?: number };
}

export async function readFinancialCashOverview(clientId: string, periodFrom: string, periodUntil: string): Promise<FinancialCashOverview> {
  const { data, error } = await supabase.rpc('financial_a7_read_cash_overview', {
    p_client_id: clientId,
    p_period_from: periodFrom,
    p_period_until: periodUntil,
  });
  if (error) {
    if (error.code === '42501') throw new Error('Seu acesso financeiro não foi autorizado para este tenant.');
    if (error.code === 'PGRST202' || error.code === '42883') throw new Error('A leitura do fluxo de caixa ainda não foi aplicada no banco. Execute o 26a.');
    throw new Error(error.message || 'Não foi possível carregar o fluxo de caixa.');
  }
  if (!data || typeof data !== 'object' || !data.totals || !Array.isArray(data.accounts) || !Array.isArray(data.movements)) {
    throw new Error('A leitura do fluxo de caixa retornou um formato inválido.');
  }
  return data as FinancialCashOverview;
}

export async function generateFinancialA7Settlements(input: { clientId: string; periodFrom: string; periodUntil: string }): Promise<{ createdItems: number; updatedSettlements: number; createdSupplemental: number; skippedLocked: number; settlementIds: string[] }> {
  const { data, error } = await supabase.rpc('financial_a13_generate_settlements', {
    p_client_id: input.clientId,
    p_period_from: input.periodFrom,
    p_period_until: input.periodUntil,
  });
  if (error) {
    if (error.code === '42501') throw new Error('Somente administradores podem gerar fechamentos de repasse.');
    if (error.code === 'PGRST202' || error.code === '42883') throw new Error('A geração de repasses com pendências e complementos ainda não foi aplicada no banco. Execute o 31a.');
    throw new Error(error.message || 'Não foi possível gerar o fechamento operacional.');
  }
  if (!data || typeof data !== 'object' || !Array.isArray(data.settlementIds)) {
    throw new Error('A geração de repasse retornou um formato inválido.');
  }
  return data as { createdItems: number; updatedSettlements: number; createdSupplemental: number; skippedLocked: number; settlementIds: string[] };
}

export async function readFinancialSettlementPending(clientId: string, periodFrom: string, periodUntil: string): Promise<FinancialSettlementPendingOverview> {
  const { data, error } = await supabase.rpc('financial_a13_read_settlement_pending', {
    p_client_id: clientId,
    p_period_from: periodFrom,
    p_period_until: periodUntil,
  });
  if (error) {
    if (error.code === '42501') throw new Error('Seu acesso financeiro não foi autorizado para este tenant.');
    if (error.code === 'PGRST202' || error.code === '42883') throw new Error('A leitura de pendências de repasse ainda não foi aplicada no banco. Execute o 31a.');
    throw new Error(error.message || 'Não foi possível consultar as pendências de repasse.');
  }
  if (!data || typeof data !== 'object' || !Array.isArray(data.records)) {
    throw new Error('A consulta de pendências de repasse retornou um formato inesperado.');
  }
  return data as FinancialSettlementPendingOverview;
}

export async function readFinancialA11Settlements(clientId: string, search: string, offset: number): Promise<FinancialPage> {
  const { data, error } = await supabase.rpc('financial_a13_read_settlements', {
    p_client_id: clientId,
    p_search: search,
    p_offset: offset,
  });
  if (error) {
    if (error.code === '42501') throw new Error('Seu acesso financeiro não foi autorizado para este tenant.');
    if (error.code === 'PGRST202' || error.code === '42883') throw new Error('O fluxo de aprovação de repasses ainda não foi aplicado no banco. Execute o 29a.');
    throw new Error(error.message || 'Não foi possível consultar os repasses.');
  }
  if (!data || !Array.isArray(data.records) || typeof data.total !== 'number' || data.pageSize !== 50) {
    throw new Error('A consulta de repasses retornou um formato inesperado.');
  }
  return data as FinancialPage;
}

export async function readFinancialA11SettlementDetail(clientId: string, settlementId: string): Promise<FinancialSettlementDetail> {
  const { data, error } = await supabase.rpc('financial_a11_read_settlement_detail', {
    p_client_id: clientId,
    p_settlement_id: settlementId,
  });
  if (error) {
    if (error.code === '42501') throw new Error('Seu acesso financeiro não foi autorizado para este tenant.');
    throw new Error(error.message || 'Não foi possível consultar o detalhe do repasse.');
  }
  if (!data || typeof data !== 'object' || typeof data.settlementId !== 'string' || !Array.isArray(data.items) || !Array.isArray(data.payments)) {
    throw new Error('O detalhe do repasse retornou um formato inesperado.');
  }
  return data as FinancialSettlementDetail;
}

export async function approveFinancialA11Settlement(clientId: string, settlementId: string, reason?: string, confirmPending = false): Promise<void> {
  const { error } = await supabase.rpc('financial_a13_approve_settlement', {
    p_client_id: clientId,
    p_settlement_id: settlementId,
    p_reason: reason?.trim() || null,
    p_confirm_pending: confirmPending,
  });
  if (error) {
    if (error.code === '42501') throw new Error('Somente administradores e usuários financeiros podem aprovar repasses.');
    if (error.code === 'PGRST202' || error.code === '42883') throw new Error('A aprovação protegida por pendências ainda não foi aplicada no banco. Execute o 31a.');
    throw new Error(error.message || 'Não foi possível aprovar o repasse.');
  }
}

export async function payFinancialA11Settlement(input: {
  clientId: string;
  settlementId: string;
  amount: number;
  paidAt: string;
  paymentMethod: string;
  cashAccountId: string;
  idempotencyKey: string;
  notes?: string;
}): Promise<{ settlementPaymentId: string; cashMovementId: string; remainingAmount: number; settlementStatus: string; idempotent?: boolean }> {
  const { data, error } = await supabase.rpc('financial_a11_pay_settlement', {
    p_client_id: input.clientId,
    p_settlement_id: input.settlementId,
    p_amount: input.amount,
    p_paid_at: input.paidAt,
    p_payment_method: input.paymentMethod,
    p_cash_account_id: input.cashAccountId,
    p_idempotency_key: input.idempotencyKey,
    p_notes: input.notes?.trim() || null,
  });
  if (error) {
    if (error.code === '42501') throw new Error('Somente administradores e usuários financeiros podem registrar pagamentos.');
    throw new Error(error.message || 'Não foi possível registrar o pagamento do repasse.');
  }
  if (!data || typeof data.settlementPaymentId !== 'string') throw new Error('O pagamento retornou um formato inválido.');
  return data as { settlementPaymentId: string; cashMovementId: string; remainingAmount: number; settlementStatus: string; idempotent?: boolean };
}

export async function reverseFinancialA11SettlementPayment(input: { clientId: string; settlementPaymentId: string; reversedAt: string; reason: string }): Promise<{ reversalPaymentId: string; settlementStatus: string }> {
  const { data, error } = await supabase.rpc('financial_a11_reverse_settlement_payment', {
    p_client_id: input.clientId,
    p_settlement_payment_id: input.settlementPaymentId,
    p_reversed_at: input.reversedAt,
    p_reason: input.reason.trim(),
  });
  if (error) {
    if (error.code === '42501') throw new Error('Somente administradores e usuários financeiros podem estornar pagamentos.');
    throw new Error(error.message || 'Não foi possível estornar o pagamento do repasse.');
  }
  if (!data || typeof data.reversalPaymentId !== 'string') throw new Error('O estorno retornou um formato inválido.');
  return data as { reversalPaymentId: string; settlementStatus: string };
}

export async function readFinancialCashAccounts(clientId: string): Promise<FinancialCashAccount[]> {
  const { data, error } = await supabase.rpc('financial_a4_read_cash_accounts', { p_client_id: clientId });
  if (error) {
    if (error.code === '42501') throw new Error('Seu acesso financeiro não foi autorizado para este tenant.');
    if (error.code === 'PGRST202' || error.code === '42883') throw new Error('A leitura das contas financeiras ainda não foi aplicada no banco. Execute o 19b.');
    throw new Error('Não foi possível carregar as contas financeiras ativas.');
  }
  if (!Array.isArray(data)) throw new Error('A leitura das contas financeiras retornou um formato inválido.');
  return data as FinancialCashAccount[];
}

export async function postFinancialReceipt(input: {
  clientId: string;
  payerPatientLinkId: string;
  amount: number;
  receivedAt: string;
  paymentMethod: string;
  cashAccountId: string;
  invoiceId: string;
  notes?: string;
}): Promise<{ receiptId: string; idempotent?: boolean }> {
  const { data, error } = await supabase.rpc('financial_a4_post_receipt', {
    p_client_id: input.clientId,
    p_payer_patient_link_id: input.payerPatientLinkId,
    p_amount: input.amount,
    p_received_at: input.receivedAt,
    p_payment_method: input.paymentMethod,
    p_cash_account_id: input.cashAccountId,
    p_allocations: [{ financialInvoiceId: input.invoiceId, amount: input.amount }],
    p_external_reference: null,
    p_actor_user_id: null,
    p_notes: input.notes || null,
  });
  if (error) {
    if (error.code === '42501') throw new Error('Seu acesso não permite registrar recebimentos.');
    throw new Error(error.message || 'Não foi possível registrar o recebimento.');
  }
  if (!data || typeof data.receiptId !== 'string') throw new Error('O recebimento foi gravado com retorno inválido.');
  return data as { receiptId: string; idempotent?: boolean };
}

export async function readFinancialInvoiceReceipts(clientId: string, invoiceId: string): Promise<FinancialReceipt[]> {
  const { data, error } = await supabase.rpc('financial_a4_read_invoice_receipts', { p_client_id: clientId, p_invoice_id: invoiceId });
  if (error) {
    if (error.code === '42501') throw new Error('Seu acesso financeiro não foi autorizado para este tenant.');
    if (error.code === 'PGRST202' || error.code === '42883') throw new Error('A leitura dos recebimentos ainda não foi aplicada no banco. Execute o 19b.');
    throw new Error('Não foi possível carregar os recebimentos da fatura.');
  }
  if (!Array.isArray(data)) throw new Error('A leitura dos recebimentos retornou um formato inválido.');
  return data as FinancialReceipt[];
}

export async function reverseFinancialReceipt(input: {
  clientId: string;
  receiptId: string;
  receivedAt: string;
  cashAccountId: string;
  notes?: string;
}): Promise<{ receiptId: string; reversedReceiptId: string }> {
  const { data, error } = await supabase.rpc('financial_a4_reverse_receipt', {
    p_client_id: input.clientId,
    p_receipt_id: input.receiptId,
    p_received_at: input.receivedAt,
    p_cash_account_id: input.cashAccountId,
    p_actor_user_id: null,
    p_notes: input.notes || null,
  });
  if (error) {
    if (error.code === '42501') throw new Error('Seu acesso não permite estornar recebimentos.');
    throw new Error(error.message || 'Não foi possível estornar o recebimento.');
  }
  if (!data || typeof data.receiptId !== 'string') throw new Error('O estorno foi gravado com retorno inválido.');
  return data as { receiptId: string; reversedReceiptId: string };
}

// Ativação da nova área financeira por tenant. A autorização efetiva de cada
// operação continua pertencendo às RPCs e políticas RLS do banco.
export function isFinancialPreviewEnabled(clientId: string): boolean {
  const tenants = (import.meta.env.VITE_FINANCIAL_A0_TENANTS ?? '')
    .split(',')
    .map((id: string) => id.trim())
    .filter(Boolean);
  return Boolean(clientId) && tenants.includes(clientId.trim());
}

export async function readFinancialPage(clientId: string, section: FinancialSection,
  parentId: string | null, search: string, offset: number, signal: AbortSignal): Promise<FinancialPage> {
  if (!clientId || !isFinancialPreviewEnabled(clientId)) throw new Error('Consulta financeira não habilitada neste ambiente e clínica.');
  if (section === 'settlements') return readFinancialA11Settlements(clientId, search, offset);
  if (section === 'settlement_items' && parentId) {
    const detail = await readFinancialA11SettlementDetail(clientId, parentId);
    const records = detail.items
      .map((row, index) => ({ ...row, id: row.id ?? row.settlementItemId ?? `${parentId}:${index}` }))
      .filter(row => !search || JSON.stringify(row).toLowerCase().includes(search.toLowerCase()));
    return { records, total: records.length, pageSize: 50 };
  }

  const rpcArguments = {
    p_client_id: clientId, p_section: section, p_parent_id: parentId,
    p_search: search, p_offset: offset,
  };
  let response = await supabase.rpc('financial_a0_read', rpcArguments).abortSignal(signal);
  if (response.error && isFinancialRpcAbort({ ...response.error, status: response.status }, signal.aborted)) {
    throw new DOMException('Consulta financeira cancelada.', 'AbortError');
  }
  let failure = response.error ? normalizeFinancialRpcError({
    ...response.error,
    status: response.status,
    statusText: response.statusText,
  }) : null;
  let tokenRefreshRetried = false;

  if (failure?.retryAfterTokenRefresh && !signal.aborted && auth.currentUser) {
    tokenRefreshRetried = true;
    await auth.currentUser.getIdToken(true).catch(() => null);
    response = await supabase.rpc('financial_a0_read', rpcArguments).abortSignal(signal);
    if (response.error && isFinancialRpcAbort({ ...response.error, status: response.status }, signal.aborted)) {
      throw new DOMException('Consulta financeira cancelada.', 'AbortError');
    }
    failure = response.error ? normalizeFinancialRpcError({
      ...response.error,
      status: response.status,
      statusText: response.statusText,
    } as FinancialRpcErrorInput) : null;
  }

  if (failure) {
    const tokenResult = await auth.currentUser?.getIdTokenResult().catch(() => null);
    const claims = tokenResult?.claims;
    console.error('[financial_a0_read] Falha na consulta financeira:', {
      diagnosticId: failure.diagnosticId,
      category: failure.category,
      status: failure.status,
      statusText: failure.statusText,
      code: failure.code,
      message: failure.message,
      details: failure.details,
      hint: failure.hint,
      section,
      clientId,
      tokenRefreshRetried,
      auth: {
        hasSub: typeof claims?.sub === 'string' && claims.sub.length > 0,
        issuer: typeof claims?.iss === 'string' ? claims.iss : null,
        audience: typeof claims?.aud === 'string' ? claims.aud : null,
        role: typeof claims?.role === 'string' ? claims.role : null,
        expiresAt: tokenResult?.expirationTime ?? null,
      },
    });
    throw new Error(failure.userMessage);
  }
  const { data } = response;
  if (!data || !Array.isArray(data.records) || typeof data.total !== 'number' || data.pageSize !== 50
    || data.records.some((r: FinancialRecord) => !r || typeof r.id !== 'string')) {
    throw new Error('A consulta retornou um formato inesperado. Nenhum saldo foi presumido.');
  }
  return data as FinancialPage;
}

export async function readFinancialAppointmentSummaries(clientId: string, appointmentIds: string[]): Promise<FinancialAppointmentSummary[]> {
  const ids = [...new Set(appointmentIds.filter(Boolean))];
  if (!ids.length) return [];
  const { data, error } = await supabase
    .from('appointments')
    .select('appointmentId, appointmentStartDate, appointmentLocation, users!appointments_userId_fkey(userDisplayName, userAssignedCalendarName), patients(patientFullName)')
    .eq('clientId', clientId)
    .in('appointmentId', ids);
  if (error) throw new Error(error.message || 'Não foi possível consultar os atendimentos do extrato.');
  return (data ?? []).map(row => {
    const relation = Array.isArray(row.users) ? row.users[0] : row.users;
    const patient = Array.isArray(row.patients) ? row.patients[0] : row.patients;
    return {
      appointmentId: String(row.appointmentId),
      appointmentStartDate: String(row.appointmentStartDate),
      appointmentLocation: row.appointmentLocation ? String(row.appointmentLocation) : null,
      professionalName: String(relation?.userDisplayName ?? relation?.userAssignedCalendarName ?? 'Profissional não informado'),
      patientName: String(patient?.patientFullName ?? 'Paciente não informado'),
    };
  });
}

export async function readFinancialA1Page(clientId: string, section: Extract<FinancialSection, 'organizations' | 'payer_links' | 'pricing_rules'>,
  parentId: string | null, search: string, offset: number, signal: AbortSignal): Promise<FinancialPage> {
  if (!clientId || !isFinancialPreviewEnabled(clientId)) throw new Error('Consulta financeira não habilitada neste ambiente e clínica.');
  const { data, error } = await supabase.rpc('financial_a1_read', {
    p_client_id: clientId, p_section: section, p_parent_id: parentId,
    p_search: search, p_offset: offset,
  }).abortSignal(signal);
  if (error) {
    if (error.code === '42501' || error.code === 'PGRST301') throw new Error('Seu acesso financeiro não foi autorizado. Confirme seu vínculo de administrador com esta clínica.');
    if (error.code === 'PGRST202' || error.code === '42883') throw new Error('A consulta A1 ainda não foi liberada no banco local. A preparação manual precisa ser concluída.');
    throw new Error('Não foi possível consultar os pagadores e contratos. Tente novamente ou solicite a verificação da configuração de acesso.');
  }
  if (!data || !Array.isArray(data.records) || typeof data.total !== 'number' || data.pageSize !== 50
    || data.records.some((r: FinancialRecord) => !r || typeof r.id !== 'string')) {
    throw new Error('A consulta A1 retornou um formato inesperado. Nenhum dado foi presumido.');
  }
  return data as FinancialPage;
}

export async function saveFinancialA1Organization(input: {
  clientId: string;
  organizationId?: string | null;
  type: 'health_plan' | 'company' | 'insurer' | 'other';
  name: string;
  document?: string;
  email?: string;
  phone?: string;
  status?: 'active' | 'inactive';
}): Promise<string> {
  const { data, error } = await supabase.rpc('financial_a1_save_organization', {
    p_client_id: input.clientId,
    p_organization_id: input.organizationId ?? null,
    p_type: input.type,
    p_name: input.name,
    p_document: input.document || null,
    p_email: input.email || null,
    p_phone: input.phone || null,
    p_status: input.status ?? 'active',
  });
  if (error) {
    if (error.code === '42501') throw new Error('Somente administradores podem cadastrar organizações.');
    if (error.code === '23505') throw new Error('Já existe uma organização com este documento neste tenant.');
    throw new Error(error.message || 'Não foi possível salvar a organização.');
  }
  if (typeof data !== 'string') throw new Error('A gravação retornou um identificador inválido.');
  return data;
}

export async function saveFinancialA1PayerLink(input: {
  clientId: string;
  linkId?: string | null;
  patientId: string;
  payerPatientId?: string | null;
  payerOrganizationId?: string | null;
  role: 'self' | 'responsible' | 'company' | 'insurer' | 'health_plan' | 'other';
  primary: boolean;
  effectiveFrom: string;
  effectiveUntil?: string;
  notes?: string;
  replacePrimaryLinkIds?: string[];
}): Promise<string> {
  const { data, error } = await supabase.rpc('financial_a1_save_payer_link_v2', {
    p_client_id: input.clientId,
    p_link_id: input.linkId ?? null,
    p_patient_id: input.patientId,
    p_payer_patient_id: input.payerPatientId ?? null,
    p_payer_organization_id: input.payerOrganizationId ?? null,
    p_role: input.role,
    p_primary: input.primary,
    p_effective_from: input.effectiveFrom,
    p_effective_until: input.effectiveUntil || null,
    p_notes: input.notes || null,
    p_replace_primary_link_ids: input.replacePrimaryLinkIds ?? null,
  });
  if (error) {
    if (error.code === '42501') throw new Error('Somente administradores podem cadastrar pagadores.');
    if (error.code === 'PPR01') {
      const conflicts = parseFinancialPrimaryPayerConflict(error.details);
      if (conflicts) throw new FinancialPrimaryPayerConflictError(conflicts);
    }
    if (error.code === 'PGRST202' || error.code === '42883') throw new Error('A atualização de pagador principal precisa ser aplicada no banco (43a).');
    throw new Error(error.message || 'Não foi possível salvar o pagador.');
  }
  if (typeof data !== 'string') throw new Error('A gravação retornou um identificador inválido.');
  return data;
}

export async function saveFinancialA1Contract(input: {
  clientId: string; contractId?: string | null; payerLinkId: string; patientId: string;
  type: string; status: string; effectiveFrom: string; effectiveUntil?: string;
  billingFrequency?: string; additionalRules?: Record<string, unknown> | null;
}): Promise<string> {
  const { data, error } = await supabase.rpc('financial_a1_save_contract', {
    p_client_id: input.clientId, p_contract_id: input.contractId ?? null,
    p_payer_link_id: input.payerLinkId, p_patient_id: input.patientId, p_type: input.type,
    p_status: input.status, p_effective_from: input.effectiveFrom,
    p_effective_until: input.effectiveUntil || null, p_billing_frequency: input.billingFrequency || null,
    p_additional_rules: input.additionalRules ?? null,
  });
  if (error) {
    if (error.code === '23505' && error.message.includes('uq_financial_legacy_contract')) {
      throw new Error(`DUPLICATE_CONTRACT: ${error.message}`);
    }
    throw new Error(error.message || 'Não foi possível salvar o contrato.');
  }
  if (typeof data !== 'string') throw new Error('A gravação retornou um identificador inválido.');
  return data;
}

export async function activateFinancialA1Contract(clientId: string, contractId: string): Promise<void> {
  const { error } = await supabase.rpc('financial_a1_activate_contract', {
    p_client_id: clientId, p_contract_id: contractId,
  });
  if (error) throw new Error(error.message || 'Não foi possível ativar o contrato.');
}

export async function keepFinancialA1ContractDraft(clientId: string, contractId: string): Promise<void> {
  const { error } = await supabase.rpc('financial_a1_keep_contract_draft', {
    p_client_id: clientId, p_contract_id: contractId,
  });
  if (error) throw new Error(error.message || 'Não foi possível manter o contrato como rascunho.');
}

export async function createFinancialA1ContractVersion(input: { clientId: string; sourceContractId: string; effectiveFrom: string; effectiveUntil?: string | null; additionalRules?: Record<string, unknown> | null }): Promise<string> {
  const { data, error } = await supabase.rpc('financial_a1_create_contract_version', {
    p_client_id: input.clientId, p_source_contract_id: input.sourceContractId,
    p_effective_from: input.effectiveFrom, p_effective_until: input.effectiveUntil || null,
    p_additional_rules: input.additionalRules ?? null,
  });
  if (error) throw new Error(error.message || 'Não foi possível criar a nova versão do contrato.');
  if (typeof data !== 'string') throw new Error('A criação da versão retornou um identificador inválido.');
  return data;
}

export async function saveFinancialA1PricingRule(input: {
  clientId: string; ruleId?: string | null; contractId: string; serviceType: string;
  procedureCode?: string; unitPrice: number; payerAmount: number; patientAmount: number;
  requiresAuthorization: boolean; authorizationType?: string; effectiveFrom: string;
  effectiveUntil?: string; status: string; additionalRules?: Record<string, unknown> | null;
}): Promise<string> {
  const { data, error } = await supabase.rpc('financial_a1_save_pricing_rule', {
    p_client_id: input.clientId, p_rule_id: input.ruleId ?? null, p_contract_id: input.contractId,
    p_service_type: input.serviceType, p_procedure_code: input.procedureCode || null,
    p_unit_price: input.unitPrice, p_payer_amount: input.payerAmount, p_patient_amount: input.patientAmount,
    p_requires_authorization: input.requiresAuthorization, p_authorization_type: input.authorizationType || null,
    p_effective_from: input.effectiveFrom, p_effective_until: input.effectiveUntil || null,
    p_status: input.status, p_additional_rules: input.additionalRules ?? null,
  });
  if (error) throw new Error(error.message || 'Não foi possível salvar a regra de preço.');
  if (typeof data !== 'string') throw new Error('A gravação retornou um identificador inválido.');
  return data;
}

export async function readFinancialServices(clientId: string, signal?: AbortSignal): Promise<FinancialRecord[]> {
  const query = supabase.rpc('financial_a1_read_services', { p_client_id: clientId });
  const { data, error } = await (signal ? query.abortSignal(signal) : query);
  if (error) throw new Error(error.message || 'Não foi possível carregar o catálogo de serviços.');
  if (!Array.isArray(data)) throw new Error('O catálogo de serviços retornou um formato inesperado.');
  return data as FinancialRecord[];
}

export async function saveFinancialService(input: { clientId: string; serviceId?: string | null; name: string; code?: string; category?: string; isActive?: boolean }): Promise<string> {
  const { data, error } = await supabase.rpc('financial_a1_save_service', {
    p_client_id: input.clientId, p_service_id: input.serviceId ?? null, p_name: input.name,
    p_code: input.code || null, p_category: input.category || null, p_is_active: input.isActive ?? true,
  });
  if (error) throw new Error(error.message || 'Não foi possível salvar o serviço.');
  if (typeof data !== 'string') throw new Error('A gravação retornou um identificador inválido.');
  return data;
}

export async function readFinancialTenantAbsencePolicies(clientId: string): Promise<FinancialTenantAbsencePoliciesResult> {
  const { data, error } = await supabase.rpc('financial_a17_read_tenant_absence_policies', { p_client_id: clientId });
  if (error) {
    if (error.code === '42501') throw new Error('Seu acesso não permite consultar a política de faltas deste tenant.');
    if (error.code === 'PGRST202' || error.code === '42883') throw new Error('A estrutura da A17.1 ainda não foi aplicada no banco. Execute o arquivo 36a.');
    throw new Error(error.message || 'Não foi possível carregar a política de faltas.');
  }
  if (!data || typeof data !== 'object' || !Array.isArray(data.history) || !data.systemDefault) {
    throw new Error('A política de faltas retornou um formato inesperado.');
  }
  return data as FinancialTenantAbsencePoliciesResult;
}

export async function saveFinancialTenantAbsencePolicy(input: {
  clientId: string;
  absencePolicyId?: string | null;
  policyStatus: 'draft' | 'active';
  values: FinancialAbsencePolicyValues;
  effectiveFrom: string;
  notes?: string;
}): Promise<string> {
  const { data, error } = await supabase.rpc('financial_a17_save_tenant_absence_policy', {
    p_client_id: input.clientId,
    p_absence_policy_id: input.absencePolicyId ?? null,
    p_policy_status: input.policyStatus,
    p_charge_from_absence: input.values.chargeFromAbsence,
    p_counting_period_type: input.values.countingPeriodType,
    p_rolling_window_days: input.values.rollingWindowDays,
    p_package_treatment: input.values.packageTreatment,
    p_individual_treatment: input.values.individualTreatment,
    p_individual_charge_percentage: input.values.individualChargePercentage,
    p_individual_fixed_amount: input.values.individualFixedAmount,
    p_justification_deadline_hours: input.values.justificationDeadlineHours,
    p_generates_professional_settlement: input.values.generatesProfessionalSettlement,
    p_effective_from: input.effectiveFrom,
    p_notes: input.notes || null,
  });
  if (error) {
    if (error.code === '42501') throw new Error('Somente administradores podem alterar a política de faltas.');
    if (error.code === 'PGRST202' || error.code === '42883') throw new Error('A estrutura da A17.1 ainda não foi aplicada no banco. Execute o arquivo 36a.');
    throw new Error(error.message || 'Não foi possível salvar a política de faltas.');
  }
  if (typeof data !== 'string') throw new Error('A gravação da política retornou um identificador inválido.');
  return data;
}

export async function readFinancialContractAbsencePolicy(clientId: string, contractId: string, referenceDate?: string): Promise<FinancialContractAbsencePolicyResult> {
  const { data, error } = await supabase.rpc('financial_a17_read_contract_absence_policy', {
    p_client_id: clientId,
    p_contract_id: contractId,
    p_reference_date: referenceDate || new Date().toISOString().slice(0, 10),
  });
  if (error) {
    if (error.code === '42501') throw new Error('Seu acesso não permite consultar esta política contratual.');
    if (error.code === 'PGRST202' || error.code === '42883') throw new Error('A estrutura da A17.1 ainda não foi aplicada no banco. Execute o arquivo 36a.');
    throw new Error(error.message || 'Não foi possível carregar a política de faltas do contrato.');
  }
  if (!data || typeof data !== 'object' || !data.effectivePolicy) throw new Error('A política efetiva do contrato retornou um formato inesperado.');
  return data as FinancialContractAbsencePolicyResult;
}

export async function saveFinancialContractAbsencePolicy(input: {
  clientId: string;
  contractId: string;
  policyMode: 'inherit' | 'no_charge' | 'custom';
  values?: FinancialAbsencePolicyValues;
  notes?: string;
}): Promise<string> {
  const values = input.policyMode === 'custom' ? input.values : undefined;
  const { data, error } = await supabase.rpc('financial_a17_save_contract_absence_policy', {
    p_client_id: input.clientId,
    p_contract_id: input.contractId,
    p_policy_mode: input.policyMode,
    p_charge_from_absence: values?.chargeFromAbsence ?? null,
    p_counting_period_type: values?.countingPeriodType ?? null,
    p_rolling_window_days: values?.rollingWindowDays ?? null,
    p_package_treatment: values?.packageTreatment ?? null,
    p_individual_treatment: values?.individualTreatment ?? null,
    p_individual_charge_percentage: values?.individualChargePercentage ?? null,
    p_individual_fixed_amount: values?.individualFixedAmount ?? null,
    p_justification_deadline_hours: values?.justificationDeadlineHours ?? null,
    p_generates_professional_settlement: values?.generatesProfessionalSettlement ?? null,
    p_notes: input.notes || null,
  });
  if (error) {
    if (error.code === '42501') throw new Error('Somente administradores podem alterar a política de faltas do contrato.');
    if (error.code === 'PGRST202' || error.code === '42883') throw new Error('A estrutura da A17.1 ainda não foi aplicada no banco. Execute o arquivo 36a.');
    throw new Error(error.message || 'Não foi possível salvar a política de faltas do contrato.');
  }
  if (typeof data !== 'string') throw new Error('A gravação da política contratual retornou um identificador inválido.');
  return data;
}

export type FinancialPackageTemplate = {
  packageTemplateId: string;
  templateCode: string | null;
  templateName: string;
  templateType: string;
  consumptionMode: string;
  creditsPerPeriod: number | null;
  defaultPrice: number;
  validityType: string;
  validityQuantity: number | null;
  validityUnit: string | null;
  periodUnit: string | null;
  periodQuantity: number | null;
  templateLockedAt: string | null;
  contractedPackages: number;
  contractedPatients: number;
  usageCount: number;
  patients: FinancialRecord[];
  usedPatients: FinancialRecord[];
  templateStatus: string;
  services: FinancialRecord[];
};

export async function readFinancialPackageTemplates(clientId: string): Promise<FinancialPackageTemplate[]> {
  const { data, error } = await supabase.rpc('financial_a5_read_package_templates_metrics', { p_client_id: clientId });
  if (error) throw new Error(error.message || 'Não foi possível carregar os modelos de pacote.');
  if (!Array.isArray(data)) throw new Error('Os modelos de pacote retornaram um formato inesperado.');
  return data as FinancialPackageTemplate[];
}

export async function saveFinancialPackageTemplateService(clientId: string, packageTemplateId: string, serviceId: string): Promise<void> {
  const { error } = await supabase.rpc('financial_a5_save_template_service', {
    p_client_id: clientId, p_package_template_id: packageTemplateId, p_service_id: serviceId,
  });
  if (error) throw new Error(error.message || 'Não foi possível associar o serviço ao modelo.');
}

export async function removeFinancialPackageTemplateService(clientId: string, linkId: string): Promise<void> {
  const { error } = await supabase.rpc('financial_a5_remove_template_service', {
    p_client_id: clientId, p_package_template_service_id: linkId,
  });
  if (error) throw new Error(error.message || 'Não foi possível remover o serviço do modelo.');
}

export async function contractFinancialPackage(input: {
  clientId: string; patientId: string; contractId: string; payerPatientLinkId: string;
  packageTemplateId: string; validFrom: string; packagePrice?: number | null; packageName?: string | null;
  billingMode?: 'upfront' | 'installments'; installmentCount?: number; firstDueDate?: string | null;
  payerAmount?: number | null; patientAmount?: number | null;
}): Promise<{ financialPackageId: string; entitlementId: string; validUntil: string | null; packageBillingPlanId: string; installmentCount: number }> {
  const { data, error } = await supabase.rpc('financial_a5_contract_package', {
    p_client_id: input.clientId, p_patient_id: input.patientId, p_contract_id: input.contractId,
    p_payer_patient_link_id: input.payerPatientLinkId, p_package_template_id: input.packageTemplateId,
    p_valid_from: input.validFrom, p_package_price: input.packagePrice ?? null, p_package_name: input.packageName || null,
    p_billing_mode: input.billingMode ?? 'upfront', p_installment_count: input.installmentCount ?? 1,
    p_first_due_date: input.firstDueDate ?? input.validFrom, p_payer_amount: input.payerAmount ?? null,
    p_patient_amount: input.patientAmount ?? null,
  });
  if (error) throw new Error(error.message || 'Não foi possível contratar o pacote.');
  return data as { financialPackageId: string; entitlementId: string; validUntil: string | null; packageBillingPlanId: string; installmentCount: number };
}

export type FinancialPackageInstallment = {
  id: string;
  installmentNumber: number;
  dueDate: string;
  amount: number;
  payerAmount: number;
  patientAmount: number;
  installmentStatus: string;
  billableItemId: string | null;
  financialInvoiceId: string | null;
  invoiceStatus: string | null;
};

export async function readFinancialPackageInstallments(clientId: string, packageId: string): Promise<FinancialPackageInstallment[]> {
  const { data, error } = await supabase.rpc('financial_a3_read_package_installments', {
    p_client_id: clientId, p_financial_package_id: packageId,
  });
  if (error) throw new Error(error.message || 'Não foi possível consultar as parcelas do pacote.');
  if (!Array.isArray(data)) throw new Error('A consulta de parcelas retornou um formato inesperado.');
  return data as FinancialPackageInstallment[];
}

export async function invoiceFinancialPackageInstallment(clientId: string, installmentId: string): Promise<{ financialInvoiceId: string; created: boolean }> {
  const { data, error } = await supabase.rpc('financial_a3_invoice_package_installment', {
    p_client_id: clientId, p_package_billing_installment_id: installmentId,
  });
  if (error) throw new Error(error.message || 'Não foi possível gerar a fatura da parcela.');
  if (!data || typeof data.financialInvoiceId !== 'string') throw new Error('A geração da fatura retornou um formato inesperado.');
  return data as { financialInvoiceId: string; created: boolean };
}

export async function invoiceFinancialBillableItem(
  clientId: string,
  billableItemId: string,
  dueDate?: string | null,
): Promise<{ financialInvoiceId: string; billableItemId: string; created: boolean; grouped: boolean; message?: string }> {
  const { data, error } = await supabase.rpc('financial_a3_invoice_billable_item', {
    p_client_id: clientId,
    p_billable_item_id: billableItemId,
    p_due_date: dueDate?.trim() || null,
  });
  if (error) throw new Error(error.message || 'Não foi possível faturar o item faturável.');
  if (!data || typeof data.financialInvoiceId !== 'string') throw new Error('O faturamento do item retornou um formato inesperado.');
  return data as { financialInvoiceId: string; billableItemId: string; created: boolean; grouped: boolean; message?: string };
}

export async function previewFinancialPackageCancellation(clientId: string, invoiceId: string): Promise<FinancialPackageCancellationPreview> {
  const { data, error } = await supabase.rpc('financial_a15_preview_package_cancellation', {
    p_client_id: clientId,
    p_financial_invoice_id: invoiceId,
  });
  if (error) {
    if (error.code === 'PGRST202' || error.code === '42883') throw new Error('O cancelamento assistido ainda não foi aplicado no banco. Execute a entrega 34a.');
    throw new Error(error.message || 'Não foi possível analisar o cancelamento do pacote.');
  }
  if (!data || !Array.isArray(data.packages)) throw new Error('A análise do cancelamento retornou um formato inesperado.');
  return data as FinancialPackageCancellationPreview;
}

export async function cancelFinancialPackageInvoice(clientId: string, invoiceId: string, packageId: string, reason: string): Promise<void> {
  const { error } = await supabase.rpc('financial_a15_split_and_cancel_package', {
    p_client_id: clientId,
    p_financial_invoice_id: invoiceId,
    p_financial_package_id: packageId,
    p_reason: reason.trim(),
  });
  if (error) {
    if (error.code === 'PGRST202' || error.code === '42883') throw new Error('O cancelamento assistido ainda não foi aplicado no banco. Execute a entrega 34a.');
    throw new Error(error.message || 'Não foi possível separar e cancelar o pacote.');
  }
}

export async function saveFinancialPackageTemplate(input: {
  clientId: string; templateId?: string | null; name: string; type: string; mode: string;
  credits?: number | null; periodUnit?: string | null; periodQuantity?: number | null;
  defaultPrice: number; validityType: string; validityQuantity?: number | null; validityUnit?: string | null;
  description?: string; status: string;
}): Promise<void> {
  const { error } = await supabase.rpc('financial_a5_save_package_template', {
    p_client_id: input.clientId, p_template_id: input.templateId ?? null, p_template_name: input.name,
    p_template_type: input.type, p_consumption_mode: input.mode, p_credits_per_period: input.credits ?? null,
    p_period_unit: input.periodUnit ?? null, p_period_quantity: input.periodQuantity ?? null,
    p_default_price: input.defaultPrice, p_validity_type: input.validityType,
    p_validity_quantity: input.validityQuantity ?? null, p_validity_unit: input.validityUnit ?? null,
    p_description: input.description || null,
    p_template_status: input.status,
  });
  if (error) throw new Error(error.message || 'Não foi possível salvar o modelo de pacote.');
}
